// One request/worker pass can serve multiple chart windows. Each subscriber owns
// its cancellation; the shared fetch is aborted only when nobody still needs it.
export class HistoryRequestCache {
  constructor({ source, process, now = () => Date.now(), maximumEntries = 24, maximumBars = 100_000 }) {
    this.source = source;
    this.process = process;
    this.now = now;
    this.maximumEntries = maximumEntries;
    this.maximumBars = maximumBars;
    this.entries = new Map();
    this.pending = new Map();
    this.cachedBars = 0;
    this.generation = 0;
  }

  load(request, { bypassCache = false } = {}) {
    const { signal, ...parameters } = request;
    if (signal?.aborted) return Promise.reject(aborted());
    const endpoint = this.source.endpoint;
    const includeCurrent = parameters.includeCurrent !== false;
    const scope = JSON.stringify([endpoint, parameters.symbolId || parameters.instrumentId || parameters.symbol,
      parameters.symbol, parameters.timeframe, parameters.endDate || null, includeCurrent]);
    const key = JSON.stringify([endpoint, parameters.symbolId || parameters.instrumentId || parameters.symbol,
      parameters.symbol, parameters.timeframe, parameters.count, parameters.endDate || null, includeCurrent]);
    const entry = this.entries.get(key);
    if (!bypassCache && entry && entry.expires > this.now()) {
      this.entries.delete(key);
      this.entries.set(key, entry);
      return Promise.resolve(entry.result);
    }
    if (!bypassCache) {
      for (const [coveredKey, covered] of this.entries) {
        if (covered.scope === scope && covered.expires > this.now() &&
            (covered.count >= parameters.count || covered.result.exhausted)) {
          this.entries.delete(coveredKey);
          this.entries.set(coveredKey, covered);
          return Promise.resolve(covered.result);
        }
      }
    }
    let operation = this.pending.get(key);
    if (!operation || operation.controller.signal.aborted) {
      const controller = new AbortController();
      const generation = this.generation;
      operation = { controller, subscribers: 0, settled: false, promise: null };
      const current = operation;
      operation.promise = (async () => {
        const response = await this.source.load({ ...parameters, signal: controller.signal });
        const bars = Array.isArray(response.bars) ? response.bars : [];
        const transformed = bars.length ? await this.process(bars) : null;
        if (controller.signal.aborted) throw aborted();
        const result = {
          data: transformed?.data || null,
          fetchDuration: response.duration,
          workerDuration: transformed?.duration || 0,
          exhausted: response.exhausted ?? (bars.length < parameters.count),
          nextEndDate: response.nextEndDate || null
        };
        if (this.generation === generation && this.source.endpoint === endpoint) {
          this.store(key, result, parameters.endDate ? 300_000 : 30_000, scope, parameters.count);
        }
        return result;
      })().catch(error => {
        if (error?.status === 401 || error?.status === 403) this.clear();
        throw error;
      }).finally(() => {
        current.settled = true;
        if (this.pending.get(key) === current) this.pending.delete(key);
      });
      this.pending.set(key, operation);
    }
    return subscribe(operation, signal);
  }

  store(key, result, ttl, scope, count) {
    this.remove(key);
    const bars = result.data?.length || 0;
    if (bars > this.maximumBars) return;
    this.entries.set(key, { result, bars, expires: this.now() + ttl, scope, count });
    this.cachedBars += bars;
    while (this.entries.size > this.maximumEntries || this.cachedBars > this.maximumBars) {
      this.remove(this.entries.keys().next().value);
    }
  }

  remove(key) {
    const entry = this.entries.get(key);
    if (entry) this.cachedBars -= entry.bars;
    this.entries.delete(key);
  }

  clear() {
    this.generation += 1;
    // Existing subscribers still receive their own authorized response or denial.
    // Detach old requests so later callers cannot join or cache pre-denial work.
    this.pending.clear();
    this.entries.clear();
    this.cachedBars = 0;
  }
}

function subscribe(operation, signal) {
  operation.subscribers += 1;
  return new Promise((resolve, reject) => {
    let finished = false;
    const finish = (callback, value) => {
      if (finished) return;
      finished = true;
      signal?.removeEventListener("abort", onAbort);
      operation.subscribers -= 1;
      if (!operation.settled && operation.subscribers === 0) operation.controller.abort();
      callback(value);
    };
    const onAbort = () => finish(reject, aborted());
    signal?.addEventListener("abort", onAbort, { once: true });
    operation.promise.then(value => finish(resolve, value), error => finish(reject, error));
  });
}

function aborted() { return new DOMException("History loading was aborted.", "AbortError"); }
